import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MediaUri, productStatusOptions } from "../../../constants/enum";
import { Product } from "../FolderView";

interface SerieGroup {
  name: string;
  count: number;
  products: Product[];
}

interface SerieGroupCluster {
  key: string;
  series: Record<string, SerieGroup>;
  totalCount: number;
}

interface HierarchyNode {
  name: string;
  count: number;
  children?: Record<string, HierarchyNode>;
  products?: Product[];
  series?: Record<string, SerieGroup>;
}

interface TreeViewProps {
  products: Product[];
  themeColors: { bg: string; border: string; text: string };
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

// Helper function to get serie key from productHierachy (first 13 characters)
const getSerieKey = (productHierachy: string | undefined): string => {
  if (!productHierachy || productHierachy.length < 13) return "Unknown Serie";
  return productHierachy.substring(0, 13);
};

// Helper function to extract product name prefix (chars before first number)
const getProductNamePrefix = (name: string | undefined): string => {
  if (!name) return "Unknown";
  const match = name.match(/^([^\d]+)/);
  return match ? match[1].trim() : "Unknown";
};

// Helper function to get last character of serie key
const getSerieGroupKey = (serieKey: string): string => {
  if (serieKey.length < 2) return serieKey;
  return serieKey.substring(serieKey.length - 1);
};

// Helper function to group series by last char + product name prefix
const groupSeriesByClusters = (series: Record<string, SerieGroup>): SerieGroupCluster[] => {
  const clusterMap: Record<string, SerieGroupCluster> = {};

  Object.entries(series).forEach(([serieKey, serieGroup]) => {
    const last2Chars = getSerieGroupKey(serieKey);
    const firstProduct = serieGroup.products[0];
    const namePrefix = getProductNamePrefix(firstProduct?.name);
    const clusterKey = `${last2Chars}|${namePrefix}`;

    if (!clusterMap[clusterKey]) {
      clusterMap[clusterKey] = {
        key: clusterKey,
        series: {},
        totalCount: 0,
      };
    }

    clusterMap[clusterKey].series[serieKey] = serieGroup;
    clusterMap[clusterKey].totalCount += serieGroup.count;
  });

  return Object.values(clusterMap).sort((a, b) => a.key.localeCompare(b.key));
};

// Helper function to build hierarchy from flat array
const buildHierarchy = (products: Product[]): Record<string, HierarchyNode> => {
  const hierarchy: Record<string, HierarchyNode> = {};

  products.forEach((product) => {
    const category = product.category || "Uncategorized";
    const subcategory = product.subcategory || "General";
    const brandType = product.brandType || "Standard";
    const serieKey = getSerieKey(product.productHierachy);

    if (!hierarchy[category]) {
      hierarchy[category] = { name: category, count: 0, children: {} };
    }
    hierarchy[category].count++;

    if (!hierarchy[category].children![subcategory]) {
      hierarchy[category].children![subcategory] = { name: subcategory, count: 0, children: {} };
    }
    hierarchy[category].children![subcategory].count++;

    if (!hierarchy[category].children![subcategory].children![brandType]) {
      hierarchy[category].children![subcategory].children![brandType] = {
        name: brandType,
        count: 0,
        series: {},
      };
    }
    hierarchy[category].children![subcategory].children![brandType].count++;

    if (!hierarchy[category].children![subcategory].children![brandType].series![serieKey]) {
      hierarchy[category].children![subcategory].children![brandType].series![serieKey] = {
        name: serieKey,
        count: 0,
        products: [],
      };
    }
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].count++;
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].products.push(product);
  });

  return hierarchy;
};

// Helper function to get status label from productStatusOptions
const getStatusLabel = (statusValue: string | undefined): string | null => {
  if (!statusValue) return null;
  const status = productStatusOptions.find((opt) => opt.value === statusValue);
  return status ? status.label : null;
};

// Status badge color mapping
const statusColors: Record<string, { bg: string; text: string }> = {
  "01": { bg: "#DBEAFE", text: "#1E40AF" },
  "02": { bg: "#D1FAE5", text: "#065F46" },
  "03": { bg: "#FEF3C7", text: "#92400E" },
  "04": { bg: "#E5E7EB", text: "#374151" },
  "05": { bg: "#FEE2E2", text: "#991B1B" },
  "06": { bg: "#F3E8FF", text: "#6B21A8" },
};

// Product Card Component (compact version for tree view)
const ProductCard = ({ product, themeColors }: { product: Product; themeColors: { bg: string; border: string; text: string } }) => {
  const images = product.images ? JSON.parse(product.images) : null;
  const firstImage = Array.isArray(images) && images.length > 0 ? images[0] : null;
  const imageUrl = firstImage ? `${MediaUri}/display/${firstImage}` : null;
  const [imageError, setImageError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<"top" | "bottom" | "left" | "right">("top");
  const cardRef = useRef<HTMLDivElement>(null);

  const statusLabel = getStatusLabel(product.status);
  const statusColor = product.status ? statusColors[product.status] : null;

  const handleClick = () => {
    const productUrl = `/product/${product.barcode}_${product.materialCode}/show`;
    window.open(productUrl, "_blank");
  };

  const handleMouseEnter = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const tooltipWidth = 256; // w-64 = 16rem = 256px
      const tooltipHeight = 200; // estimated height
      const margin = 4; // reduced margin for closer positioning

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top: number;
      let left: number;

      // Center tooltip horizontally relative to card
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

      // Ensure tooltip doesn't go off screen horizontally
      if (left < margin) {
        left = margin;
      } else if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      // Priority: Top first, then Bottom, then Right, then Left
      let placement: "top" | "bottom" | "left" | "right" = "top";

      if (rect.top - margin - tooltipHeight > 0) {
        // Place above
        top = ( rect.top - tooltipHeight - margin) * 1.1;
        placement = "top";
      } else if (rect.bottom + margin + tooltipHeight < viewportHeight) {
        // Place below
        top = rect.bottom + margin;
        placement = "bottom";
      } else if (rect.right + margin + tooltipWidth < viewportWidth) {
        // Place right
        top = rect.top;
        left = rect.right + margin;
        placement = "right";
      } else if (rect.left - margin - tooltipWidth > 0) {
        // Place left
        top = rect.top;
        left = rect.left - margin - tooltipWidth;
        placement = "left";
      } else {
        // Default: place above and adjust
        top = margin;
        placement = "top";
      }

      setTooltipPosition({ top, left });
      setTooltipPlacement(placement);
    }
    setShowTooltip(true);
  };

  // Check if there's any content to show in tooltip
  const hasTooltipContent = product.titleEN || product.titleTH || product.description || product.descriptionEN || product.descriptionTH;

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col items-center p-2 transition-shadow bg-white border border-gray-100 rounded-lg shadow-sm cursor-pointer hover:shadow-md"
      style={{ width: "110px" }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip using Portal */}
      {showTooltip && hasTooltipContent && createPortal(
        <div
          className="fixed z-[9999] w-64 p-3 text-left bg-gray-900 rounded-lg shadow-xl text-white text-[11px] leading-relaxed"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          {/* Arrow - position based on tooltip placement */}
          {tooltipPlacement === "top" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -bottom-1" />
          )}
          {tooltipPlacement === "bottom" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -top-1" />
          )}
          {tooltipPlacement === "right" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -left-1 top-4" />
          )}
          {tooltipPlacement === "left" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -right-1 top-4" />
          )}

          {product.titleEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title EN:</span>
              <p className="mt-0.5">{product.titleEN}</p>
            </div>
          )}
          {product.titleTH && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title TH:</span>
              <p className="mt-0.5">{product.titleTH}</p>
            </div>
          )}
          {product.description && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Description:</span>
              <p className="mt-0.5">{product.description}</p>
            </div>
          )}
          {product.descriptionEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Desc EN:</span>
              <p className="mt-0.5">{product.descriptionEN}</p>
            </div>
          )}
          {product.descriptionTH && (
            <div>
              <span className="font-semibold text-gray-300">Desc TH:</span>
              <p className="mt-0.5">{product.descriptionTH}</p>
            </div>
          )}
        </div>,
        document.body
      )}

      {statusLabel && statusColor && (
        <div
          className="absolute top-1 right-1 px-1 py-0.5 text-[8px] font-medium rounded"
          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
        >
          {statusLabel}
        </div>
      )}
      <div className="flex items-center justify-center w-full h-16 mb-1 rounded bg-gray-50">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={product.name || "Product"}
            className="object-contain w-full h-full"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-xs text-gray-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between flex-1 w-full text-center">
        <p className="h-7 text-[10px] font-medium leading-tight text-gray-700 line-clamp-2">
          {product.name || product.materialName || "N/A"}
        </p>
        <p
          className="text-[9px] font-mono px-1 py-0.5 rounded"
          style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
        >
          {product.materialCode}
        </p>
      </div>
    </div>
  );
};

// Tree Node Component - Binary Tree Style
const TreeNode = ({
  label,
  count,
  level,
  themeColors,
  children,
  isRoot = false,
  nextLevelColor,
}: {
  label: string;
  count: number;
  level: number;
  themeColors: { bg: string; border: string; text: string };
  children?: React.ReactNode;
  isRoot?: boolean;
  nextLevelColor?: string;
}) => {
  const levelColors = [
    { bg: themeColors.bg, text: themeColors.text, border: themeColors.border },
    { bg: "#E0E7FF", text: "#3730A3", border: "#6366F1" },
    { bg: "#D1FAE5", text: "#065F46", border: "#10B981" },
    { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B" },
  ];

  const colors = levelColors[level] || levelColors[levelColors.length - 1];
  // Use next level color for the connector line going down to children
  const connectorColor = nextLevelColor || levelColors[level + 1]?.border || colors.border;

  return (
    <div className="flex flex-col items-center">
      {/* Node */}
      <div
        className={`flex flex-col items-center justify-center px-4 py-2 rounded-lg border-2 shadow-md ${isRoot ? "min-w-[120px]" : "min-w-[100px]"}`}
        style={{
          backgroundColor: colors.bg,
          borderColor: colors.border,
        }}
      >
        <span
          className={`font-semibold text-center ${isRoot ? "text-sm" : "text-xs"}`}
          style={{ color: colors.text }}
        >
          {label}
        </span>
        <span
          className="px-2 py-0.5 mt-1 text-xs font-bold rounded-full bg-white/80"
          style={{ color: colors.text }}
        >
          {count.toLocaleString()}
        </span>
      </div>

      {/* Children with connector lines */}
      {children && (
        <div className="flex flex-col items-center">
          {/* Vertical line down from parent to horizontal line */}
          <div
            className="w-0.5 h-6"
            style={{ backgroundColor: connectorColor }}
          />
          {children}
        </div>
      )}
    </div>
  );
};

// Children Container with horizontal spread
const ChildrenContainer = ({
  children,
  themeColors,
  level,
}: {
  children: React.ReactNode;
  themeColors: { bg: string; border: string; text: string };
  level: number;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineStyle, setLineStyle] = useState({ left: 0, width: 0 });

  const levelColors = [
    themeColors.border,
    "#6366F1",
    "#10B981",
    "#F59E0B",
  ];
  const lineColor = levelColors[level] || levelColors[levelColors.length - 1];

  // Calculate horizontal line position based on first and last child
  const updateLinePosition = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      // Skip the first div (horizontal line) and get actual children
      const allDivs = container.querySelectorAll(':scope > div');
      const childElements = Array.from(allDivs).filter(
        (el) => !el.classList.contains('absolute')
      );

      if (childElements.length > 1) {
        const firstChild = childElements[0] as HTMLElement;
        const lastChild = childElements[childElements.length - 1] as HTMLElement;

        const containerRect = container.getBoundingClientRect();
        const firstRect = firstChild.getBoundingClientRect();
        const lastRect = lastChild.getBoundingClientRect();

        // Calculate center of first and last child relative to container
        const firstCenter = firstRect.left + firstRect.width / 2 - containerRect.left;
        const lastCenter = lastRect.left + lastRect.width / 2 - containerRect.left;

        setLineStyle({
          left: firstCenter,
          width: lastCenter - firstCenter,
        });
      } else if (childElements.length === 1) {
        // Single child - no horizontal line needed
        setLineStyle({ left: 0, width: 0 });
      }
    }
  };

  // Update line position on mount, when children change, and on resize/DOM changes
  useEffect(() => {
    // Initial calculation
    const timer = setTimeout(updateLinePosition, 0);

    // Watch for size changes (when children expand/collapse)
    const resizeObserver = new ResizeObserver(() => {
      updateLinePosition();
    });

    // Watch for DOM changes (when new elements are added/removed)
    const mutationObserver = new MutationObserver(() => {
      updateLinePosition();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [children]);

  return (
    <div className="relative flex flex-col items-center">
      {/* Children row with horizontal line */}
      <div ref={containerRef} className="relative flex flex-wrap justify-center gap-4">
        {/* Horizontal line connecting all children - positioned at top to connect with vertical lines */}
        {lineStyle.width > 0 && (
          <div
            className="absolute h-0.5"
            style={{
              backgroundColor: lineColor,
              left: lineStyle.left,
              width: lineStyle.width,
              top: 0,
            }}
          />
        )}
        {children}
      </div>
    </div>
  );
};

// Series Cluster Component for tree view - compact
const SeriesCluster = ({
  cluster,
  themeColors,
}: {
  cluster: SerieGroupCluster;
  themeColors: { bg: string; border: string; text: string };
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="p-2 border-2 rounded-lg"
      style={{ borderColor: themeColors.border, backgroundColor: `${themeColors.bg}40` }}
    >
      {/* Cluster Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full gap-2"
      >
        <div className="flex items-center gap-2">
          <span
            className="px-2 py-0.5 text-[10px] font-bold rounded"
            style={{ backgroundColor: themeColors.text, color: "white" }}
          >
            {cluster.key.split("|")[1] || "Unknown"}
          </span>
          <span className="text-[10px] text-gray-600">
            ({cluster.totalCount})
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          style={{ color: themeColors.text }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="flex flex-wrap items-start gap-2 mt-2">
          {Object.entries(cluster.series)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([serieKey, serieGroup]) => (
              <div key={serieKey} className="inline-flex flex-col p-2 bg-white border border-gray-200 rounded-lg">
                {/* Serie Header */}
                <div className="flex items-center gap-1 mb-1">
                  <span
                    className="px-1.5 py-0.5 text-[9px] font-semibold rounded"
                    style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
                  >
                    {serieKey}
                  </span>
                  <span className="text-[9px] text-gray-500">
                    ({serieGroup.count})
                  </span>
                </div>

                {/* Products */}
                <div className="flex flex-wrap gap-1">
                  {serieGroup.products.map((product, idx) => (
                    <ProductCard
                      key={product.id || idx}
                      product={product}
                      themeColors={themeColors}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// Brand Type Node with Series
const BrandTypeNode = ({
  btKey,
  btNode,
  themeColors,
}: {
  btKey: string;
  btNode: HierarchyNode;
  themeColors: { bg: string; border: string; text: string };
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const clusters = btNode.series ? groupSeriesByClusters(btNode.series) : [];

  return (
    <div className="relative flex flex-col items-center pt-4">
      {/* Vertical line from horizontal connector to node */}
      <div
        className="absolute w-0.5 left-1/2 -translate-x-1/2 bg-emerald-500"
        style={{ top: 0, height: '1rem' }}
      />

      {/* Node */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex flex-col items-center justify-center px-3 py-2 transition-transform bg-emerald-100 border-2 border-emerald-500 rounded-lg shadow-md hover:scale-105 min-w-[90px]"
      >
        <span className="text-xs font-semibold text-center text-emerald-800">
          {btKey}
        </span>
        <span className="px-2 py-0.5 mt-1 text-xs font-bold rounded-full bg-white/80 text-emerald-700">
          {btNode.count.toLocaleString()}
        </span>
        <svg
          className={`w-3 h-3 mt-1 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Series Clusters */}
      {isExpanded && clusters.length > 0 && (
        <div className="flex flex-col items-center">
          {/* Vertical connector line from BrandType node to Series container */}
          <div className="w-0.5 h-6 bg-amber-500" />
          <div className="grid gap-2 p-2 border-2 rounded-lg border-amber-400 bg-amber-50" style={{ maxWidth: "980px" }}>
            {clusters.map((cluster) => (
              <SeriesCluster
                key={cluster.key}
                cluster={cluster}
                themeColors={themeColors}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function TreeView({ products, themeColors, searchTerm, setSearchTerm }: TreeViewProps) {
  // Build hierarchy from products
  const hierarchy = useMemo(() => buildHierarchy(products), [products]);

  // Summary stats
  const stats = useMemo(() => {
    const categories = Object.keys(hierarchy).length;
    let subcategories = 0;
    let brandTypes = 0;
    let series = 0;

    Object.values(hierarchy).forEach((cat) => {
      if (cat.children) {
        subcategories += Object.keys(cat.children).length;
        Object.values(cat.children).forEach((sub) => {
          if (sub.children) {
            brandTypes += Object.keys(sub.children).length;
            Object.values(sub.children).forEach((bt) => {
              if (bt.series) {
                series += Object.keys(bt.series).length;
              }
            });
          }
        });
      }
    });

    return { categories, subcategories, brandTypes, series, products: products.length };
  }, [hierarchy, products]);

  const sortedCategories = Object.entries(hierarchy).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="mt-4">
      {/* Search and Stats Bar */}
      <div className="p-4 mb-4 bg-white rounded-lg shadow">
        <div className="flex flex-col gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <svg
              className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by name, material code, barcode, category, subcategory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-2 pl-10 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": themeColors.border } as React.CSSProperties}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Bottom row: Stats */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.categories}</strong> Categories
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.subcategories}</strong> Sub-categories
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.brandTypes}</strong> Brand Types
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.series}</strong> Series
            </span>
            <span
              className="px-2 py-1 font-medium rounded"
              style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
            >
              <strong>{stats.products.toLocaleString()}</strong> Products
            </span>
          </div>
        </div>
      </div>

      {/* Binary Tree Structure */}
      <div className="p-6 overflow-x-auto bg-white rounded-lg shadow">
        <div className="flex flex-col items-center min-w-max">
          {/* Root: Brand */}
          <TreeNode
            label="Products"
            count={products.length}
            level={0}
            themeColors={themeColors}
            isRoot
            nextLevelColor={themeColors.border}
          >
            {/* Categories */}
            <ChildrenContainer themeColors={themeColors} level={0}>
              {sortedCategories.map(([categoryKey, categoryNode]) => (
                <div key={categoryKey} className="relative flex flex-col items-center pt-4">
                  {/* Vertical line from horizontal connector to node */}
                  <div
                    className="absolute w-0.5 left-1/2 -translate-x-1/2"
                    style={{
                      backgroundColor: themeColors.border,
                      top: 0,
                      height: '1rem', // Connect from top to where node starts (pt-4 = 1rem)
                    }}
                  />
                  <TreeNode
                    label={categoryKey}
                    count={categoryNode.count}
                    level={0}
                    themeColors={themeColors}
                  >
                    {/* Subcategories */}
                    {categoryNode.children && Object.keys(categoryNode.children).length > 0 && (
                      <ChildrenContainer themeColors={themeColors} level={1}>
                        {Object.entries(categoryNode.children)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([subKey, subNode]) => (
                            <div key={subKey} className="relative flex flex-col items-center pt-4">
                              {/* Vertical line from horizontal connector to node */}
                              <div
                                className="absolute w-0.5 left-1/2 -translate-x-1/2 bg-indigo-500"
                                style={{ top: 0, height: '1rem' }}
                              />
                              <TreeNode
                                label={subKey}
                                count={subNode.count}
                                level={1}
                                themeColors={themeColors}
                              >
                                {/* Brand Types */}
                                {subNode.children && Object.keys(subNode.children).length > 0 && (
                                  <ChildrenContainer themeColors={themeColors} level={2}>
                                    {Object.entries(subNode.children)
                                      .sort(([a], [b]) => a.localeCompare(b))
                                      .map(([btKey, btNode]) => (
                                        <BrandTypeNode
                                          key={btKey}
                                          btKey={btKey}
                                          btNode={btNode}
                                          themeColors={themeColors}
                                        />
                                      ))}
                                  </ChildrenContainer>
                                )}
                              </TreeNode>
                            </div>
                          ))}
                      </ChildrenContainer>
                    )}
                  </TreeNode>
                </div>
              ))}
            </ChildrenContainer>
          </TreeNode>
        </div>
      </div>

      {/* No Results */}
      {Object.keys(hierarchy).length === 0 && searchTerm && (
        <div className="py-12 text-center bg-white rounded-lg shadow">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-lg text-gray-500">No products found for "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm("")}
            className="px-4 py-2 mt-4 text-sm transition-colors rounded-lg"
            style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
}

export default TreeView;
